import Axios from "axios";
import {appConfig} from "./app-config";
import {isWorkspaceScopedMutation} from "./api-request-guards";
import {getUser, setUser} from "./storage";
import {
    addErrorBreadcrumb,
    captureApiError,
    createApiRequestId,
} from "./error-monitoring";

export {isWorkspaceScopedMutation} from "./api-request-guards";

let errorHandler = null;
let unauthorizedHandler = null;
let workspaceMutationGuard = null;
let refreshPromise = null;


const axiosInstance = Axios.create({
    baseURL: appConfig.apiUrl,
    // timeout: 20000,
    headers: {Accept: "application/json"},
});

export const setHeaderToken = (token) => {

    if (token){
        axiosInstance.defaults.headers = {...axiosInstance.defaults.headers, Authorization:token};
    }
    else {
        delete axiosInstance.defaults.headers['Authorization'];
    }
};

export const getAuthToken = (user) => user?.jwt || user?.token;

export const refreshAuthSession = async () => {
    const storedUser = getUser();
    if (!storedUser?.refreshToken) {
        throw new Error("user-session.error.refresh-token-missing");
    }

    if (!refreshPromise) {
        refreshPromise = Axios.post(`${appConfig.apiUrl}/auth/refresh`, {
            refreshToken: storedUser.refreshToken,
        }, {headers: {Accept: "application/json"}}).then(({data}) => {
            const nextUser = {...getUser(), ...data};
            setUser(nextUser);
            setHeaderToken(`Bearer ${nextUser.jwt}`);
            window.dispatchEvent(new CustomEvent("zofino:auth-refreshed", {detail: nextUser}));
            return nextUser;
        }).finally(() => {
            refreshPromise = null;
        });
    }

    return refreshPromise;
};

export const normalizeApiError = (error) => {
    const data = error?.response?.data;
    const backendError = data?.error;
    const details = backendError?.details || {};
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    const rawMessage =
        data?.error?.message ||
        data?.error ||
        data?.message ||
        error?.message ||
        "api-error";
    // Generic server/network failures come in English from the server or the browser; show a Persian message
    // instead (specific backend messages, e.g. validation keys, are kept as they are).
    const isNetworkError = !error?.response && (error?.code === "ERR_NETWORK" || error?.message === "Network Error");
    const genericServerError = status >= 500 && (
        !backendError?.message || /^(internal server error|bad gateway|service unavailable|gateway timeout)$/i.test(String(backendError.message))
    );
    const message = isNetworkError
        ? "errors.network"
        : error?.code === "ECONNABORTED"
            ? "errors.timeout"
            : genericServerError
                ? "errors.server"
                : typeof rawMessage === "string" && /^request failed with status code/i.test(rawMessage)
                    ? "errors.unexpected"
                    : rawMessage;

    return {
        status,
        data,
        url,
        message,
        code: details?.code || backendError?.code || error?.code,
        details,
        retryable: Boolean(details?.retryable),
        runId: details?.runId || null,
        stepId: details?.stepId || null,
        isNetworkError,
        original: error,
    };
};

export const setApiErrorHandler = (handler) => {
    errorHandler = handler;
};

export const setApiUnauthorizedHandler = (handler) => {
    unauthorizedHandler = handler;
};

export const setWorkspaceMutationGuard = (handler) => {
    workspaceMutationGuard = typeof handler === "function" ? handler : null;
};

axiosInstance.interceptors.request.use((config) => {
    const headers = {...config.headers};
    if (!headers["X-Request-ID"] && !headers["x-request-id"]) {
        headers["X-Request-ID"] = createApiRequestId();
    }
    config.headers = headers;
    addErrorBreadcrumb("api.request", {
        method: config.method,
        status: "started",
        apiRequestId: headers["X-Request-ID"],
    });
    if (!workspaceMutationGuard || !isWorkspaceScopedMutation(config)) return config;

    const result = workspaceMutationGuard(config);
    const allowed = typeof result === "boolean" ? result : result?.allowed !== false;
    if (allowed) return config;

    const error = new Error(result?.message || "no-permission");
    error.code = "WORKSPACE_READ_ONLY";
    error.config = config;
    return Promise.reject(error);
});

axiosInstance.interceptors.response.use(
    response => {
        addErrorBreadcrumb("api.response", {
            method: response?.config?.method,
            status: response?.status,
            apiRequestId: response?.headers?.["x-request-id"],
        });
        return response;
    },
    async error => {
        const normalizedError = normalizeApiError(error);
        error.normalizedError = normalizedError;
        error.message = normalizedError.message;
        captureApiError(error);

        const request = error?.config || {};
        const storedUser = getUser();
        const canRefresh = normalizedError.status === 401
            && !request._sessionRefreshAttempted
            && !String(request.url || "").includes("/auth/refresh")
            && storedUser?.refreshToken;
        if (canRefresh) {
            request._sessionRefreshAttempted = true;
            try {
                const refreshedUser = await refreshAuthSession();
                request.headers = {...request.headers, Authorization: `Bearer ${refreshedUser.jwt}`};
                return axiosInstance(request);
            } catch (refreshError) {
                const refreshFailure = normalizeApiError(refreshError);
                if (unauthorizedHandler) unauthorizedHandler({...refreshFailure, message: normalizedError.message, code: normalizedError.code});
                return Promise.reject(error);
            }
        }

        if (normalizedError.status === 401 && unauthorizedHandler) {
            unauthorizedHandler(normalizedError);
        }
        else if (errorHandler && !error?.config?.suppressGlobalErrorHandler) {
            errorHandler(normalizedError);
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;
